/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable jsx-a11y/alt-text */
import { CircularProgress, Divider, List, ListItem } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';

import type { ProjectMetadata } from '../../../../containers/pages/project/ProjectPage.container';
import type { MovieRound } from '../../../../hooks/movie-voting.hooks';
import { Button } from '../../../core/button';
import { Panel } from '../../panel/Panel.component';
import styles from './CollectUnclaimedRewardsModal.module.css';

interface Props {
  previousRoundsVotingState: MovieRound[];
  startingRoundNum: number;
  totalRounds: number;
  userStarsRewardsPerRound: {
    [roundId: string]: string;
  };
  movieMetadata: {
    [movieId: string]: ProjectMetadata;
  };
  handleClaimRewards: (roundId: string) => Promise<boolean>;
  hideModal: () => void;
}

interface CollectUnclaimedRewardsProps {
  hideModal: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      height: '420px',
      overflowY: 'scroll',
      background: '#38435b',
    },
    unclaimedStars: {
      width: '100%',
      marginBottom: '20px',
    },
    inline: {
      display: 'inline',
    },
  }),
);

const CollectUnclaimedRewards: React.FC<Props> = ({
  previousRoundsVotingState,
  startingRoundNum,
  totalRounds,
  userStarsRewardsPerRound: userStarsRewards,
  movieMetadata,
  handleClaimRewards,
  hideModal,
}: Props) => {
  const classes = useStyles();
  const [claimingRewards, setClaimingRewards] = useState(false);

  const unClaimedRewardsData = previousRoundsVotingState.map((roundData, i) => {
    const roundId = roundData.roundId.toString();
    const hasRewards = userStarsRewards[roundId] !== '0';
    if (!hasRewards) {
      return;
    }

    let winningMovieIndex = 0;
    const numberVotes = roundData.votes.map(vote => parseInt(vote, 10));
    winningMovieIndex = numberVotes.indexOf(Math.max(...numberVotes));

    const data = movieMetadata[roundData.movieIds[winningMovieIndex]];

    const claimRewardsOnClick = async () => {
      setClaimingRewards(true);
      const result = await handleClaimRewards(roundId);
      if (result) {
        hideModal();
        window.location.reload();
      }
      setClaimingRewards(false);
    };

    return (
      <div
        key={data.name + roundId}
        className="flex-col items-center justify-center mb-5"
      >
        <ListItem className={classes.unclaimedStars}>
          <img src={data.image} className={styles.image} />
          <div className="w-full">
            <div className="w-full flex justify-between items-center">
              <div className={styles.round}>
                {`ROUND ${
                  previousRoundsVotingState.length - i + startingRoundNum
                }`}{' '}
                OF {` ${totalRounds}`}
              </div>
              <Button
                onClick={claimRewardsOnClick}
                className="h-1"
                disabled={claimingRewards}
              >
                {claimingRewards ? (
                  <CircularProgress style={{ color: 'white' }} />
                ) : (
                  'COLLECT REWARDS'
                )}
              </Button>
            </div>
            <div className={styles.title}>{data.name}</div>
            <div className={styles.container}>
              <div className={styles.label}>Your rewards</div>
              <div>{userStarsRewards[roundId]} STARS</div>
            </div>
          </div>
        </ListItem>
        <Divider variant="middle" component="li" />
      </div>
    );
  });

  return (
    <Panel
      title="Collect Unclaimed stars"
      className={styles.collectUnclaimedRewardsModal}
      bodyClassName="flex flex-col justify-center rounded-lg"
      hasExitModalButton={true}
      hideModal={hideModal}
    >
      <List className={classes.root}>{unClaimedRewardsData}</List>
    </Panel>
  );
};

export const CollectUnclaimedRewardsModal =
  (
    previousRoundsVotingState: MovieRound[],
    startingRoundNum: number,
    totalRounds: number,
    userStarsRewards: {
      [roundId: string]: string;
    },
    movieMetadata: {
      [movieId: string]: ProjectMetadata;
    },
    handleClaimRewards: (roundId: string) => Promise<boolean>,
  ) =>
  ({ hideModal }: CollectUnclaimedRewardsProps) => {
    return (
      <CollectUnclaimedRewards
        hideModal={hideModal}
        previousRoundsVotingState={previousRoundsVotingState}
        startingRoundNum={startingRoundNum}
        totalRounds={totalRounds}
        userStarsRewardsPerRound={userStarsRewards}
        movieMetadata={movieMetadata}
        handleClaimRewards={handleClaimRewards}
      />
    );
  };
